<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: string | number | null
  label?: string
  /** Expliziter Accessible Name (WCAG). Fallback: label → placeholder. */
  ariaLabel?: string
  placeholder?: string
  type?: string
  variant?: 'outlined' | 'filled' | 'underlined' | 'solo' | 'solo-filled'
  density?: 'default' | 'comfortable' | 'compact'
  hideDetails?: boolean | 'auto'
  disabled?: boolean
  readonly?: boolean
  clearable?: boolean
  prependIcon?: string
  appendIcon?: string
  prependInnerIcon?: string
  appendInnerIcon?: string
  hint?: string
  persistentHint?: boolean
  errorMessages?: string | string[]
  rules?: ((v: any) => true | string)[]
  counter?: boolean | number
  maxlength?: number
  autofocus?: boolean
  loading?: boolean
  bgColor?: string
  color?: string
  singleLine?: boolean
  rounded?: boolean | string
  flat?: boolean
  prefix?: string
  suffix?: string
}>(), {
  type: 'text',
  variant: 'outlined',
  density: 'default',
})

const emit = defineEmits<{
  'update:modelValue': [val: string | number | null]
  'click:clear': []
  'click:prepend': []
  'click:append': []
  'click:appendInner': []
  'click:prependInner': []
  'click:append-inner': []
  'click:prepend-inner': []
  keydown: [e: KeyboardEvent]
  focus: [e: FocusEvent]
  blur: [e: FocusEvent]
}>()

const inputRef = ref<HTMLInputElement>()
const focused = ref(false)
const internalError = ref('')

const errorList = computed(() => {
  if (props.errorMessages) {
    return Array.isArray(props.errorMessages) ? props.errorMessages : [props.errorMessages]
  }
  if (internalError.value) return [internalError.value]
  return []
})

const hasError = computed(() => errorList.value.length > 0)

const roundedClass = computed(() => {
  if (!props.rounded) return ''
  if (props.rounded === true) return 'm-text-field--rounded'
  if (props.rounded === 'pill') return 'm-text-field--rounded-pill'
  return 'm-text-field--rounded'
})

// Map effective variant (solo-filled = solo + filled bg)
const effectiveVariant = computed(() =>
  props.variant === 'solo-filled' ? 'solo-filled' : props.variant
)

function validate() {
  if (!props.rules) return true
  for (const rule of props.rules) {
    const result = rule(props.modelValue)
    if (result !== true) {
      internalError.value = result
      return false
    }
  }
  internalError.value = ''
  return true
}

function onInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  emit('update:modelValue', props.type === 'number' ? (val ? Number(val) : null) : val)
  if (internalError.value) validate()
}

function onClear() {
  emit('update:modelValue', props.type === 'number' ? null : '')
  emit('click:clear')
  inputRef.value?.focus()
}

function onClickAppendInner() {
  emit('click:appendInner')
  emit('click:append-inner')
}

function onClickPrependInner() {
  emit('click:prependInner')
  emit('click:prepend-inner')
}

function focus() { inputRef.value?.focus() }

defineExpose({ focus, validate, inputRef })
</script>

<template>
  <div
    class="m-text-field"
    :class="[
      `m-text-field--${effectiveVariant}`,
      `m-text-field--${density}`,
      roundedClass,
      {
        'm-text-field--focused': focused,
        'm-text-field--error': hasError,
        'm-text-field--disabled': disabled,
        'm-text-field--has-value': modelValue != null && modelValue !== '',
        'm-text-field--single-line': singleLine,
        'm-text-field--flat': flat,
      }
    ]"
  >
    <div v-if="prependIcon" class="m-text-field__prepend" @click="emit('click:prepend')">
      <MIcon :icon="prependIcon" />
    </div>

    <div class="m-text-field__control">
      <label v-if="label && !singleLine" class="m-text-field__label">{{ label }}</label>

      <div class="m-text-field__field">
        <span v-if="prependInnerIcon" class="m-text-field__prepend-inner" @click="onClickPrependInner">
          <MIcon :icon="prependInnerIcon" size="small" />
        </span>

        <span v-if="prefix" class="m-text-field__prefix">{{ prefix }}</span>

        <input
          ref="inputRef"
          :type="type"
          :value="modelValue"
          :placeholder="placeholder || (singleLine ? label : undefined)"
          :aria-label="ariaLabel || label || placeholder || undefined"
          :aria-invalid="hasError || undefined"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
          :autofocus="autofocus"
          class="m-text-field__input"
          @input="onInput"
          @focus="focused = true; emit('focus', $event)"
          @blur="focused = false; validate(); emit('blur', $event)"
          @keydown="emit('keydown', $event)"
        />

        <span v-if="suffix" class="m-text-field__suffix">{{ suffix }}</span>

        <button v-if="clearable && modelValue" class="m-text-field__clear" @click="onClear" tabindex="-1">
          <MIcon icon="close" size="small" />
        </button>

        <span v-if="appendInnerIcon" class="m-text-field__append-inner" @click="onClickAppendInner">
          <MIcon :icon="appendInnerIcon" size="small" />
        </span>

        <slot name="append-inner" />
      </div>

      <div v-if="loading" class="m-text-field__loader">
        <div class="m-text-field__progress" />
      </div>
    </div>

    <div v-if="appendIcon" class="m-text-field__append" @click="emit('click:append')">
      <MIcon :icon="appendIcon" />
    </div>

    <div v-if="hideDetails !== true" class="m-text-field__details">
      <template v-if="hasError">
        <span class="m-text-field__error">{{ errorList[0] }}</span>
      </template>
      <template v-else-if="hint && (persistentHint || focused)">
        <span class="m-text-field__hint">{{ hint }}</span>
      </template>
      <span v-if="counter" class="m-text-field__counter">
        {{ String(modelValue || '').length }}{{ typeof counter === 'number' ? ` / ${counter}` : '' }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.m-text-field {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--m-space-2);
  width: 100%;
}

.m-text-field__control {
  flex: 1;
  min-width: 0;
  position: relative;
}

.m-text-field__label {
  display: block;
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  color: var(--m-on-surface-variant);
  margin-bottom: var(--m-space-1);
  transition: color var(--m-transition);
}

.m-text-field--focused .m-text-field__label { color: var(--m-primary); }
.m-text-field--error .m-text-field__label { color: var(--m-error); }

.m-text-field__field {
  display: flex;
  align-items: center;
  gap: var(--m-space-2);
  width: 100%;
  transition: all var(--m-transition);
}

/* Outlined variant */
.m-text-field--outlined .m-text-field__field {
  border: 1px solid var(--m-border);
  border-radius: var(--m-radius);
  background: var(--m-surface);
  padding: 0 var(--m-space-3);
}
.m-text-field--outlined.m-text-field--focused .m-text-field__field { border-color: var(--m-primary); box-shadow: 0 0 0 1px var(--m-primary); }
.m-text-field--outlined.m-text-field--error .m-text-field__field { border-color: var(--m-error); box-shadow: 0 0 0 1px var(--m-error); }

/* Filled variant */
.m-text-field--filled .m-text-field__field {
  border: none;
  border-bottom: 1px solid var(--m-border);
  border-radius: var(--m-radius) var(--m-radius) 0 0;
  background: var(--m-surface-variant);
  padding: 0 var(--m-space-3);
}
.m-text-field--filled.m-text-field--focused .m-text-field__field { border-bottom-color: var(--m-primary); }

/* Solo variant */
.m-text-field--solo .m-text-field__field {
  border: none;
  border-radius: var(--m-radius);
  background: var(--m-surface);
  box-shadow: var(--m-shadow);
  padding: 0 var(--m-space-3);
}
.m-text-field--flat.m-text-field--solo .m-text-field__field { box-shadow: none; }

/* Solo-filled variant (solo shape + filled bg, no shadow) */
.m-text-field--solo-filled .m-text-field__field {
  border: none;
  border-radius: var(--m-radius);
  background: var(--m-surface-variant);
  padding: 0 var(--m-space-3);
}
.m-text-field--flat.m-text-field--solo-filled .m-text-field__field { box-shadow: none; }

/* Underlined variant */
.m-text-field--underlined .m-text-field__field {
  border: none;
  border-bottom: 1px solid var(--m-border);
  border-radius: 0;
  padding: 0;
}
.m-text-field--underlined.m-text-field--focused .m-text-field__field { border-bottom-color: var(--m-primary); }

/* Rounded */
.m-text-field--rounded .m-text-field__field { border-radius: var(--m-radius-lg, 12px); }
.m-text-field--rounded-pill .m-text-field__field { border-radius: 9999px; }

.m-text-field__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--m-font-body);
  font-size: var(--m-font-size-base);
  color: var(--m-on-surface);
  width: 100%;
}

/* Density */
.m-text-field--default .m-text-field__input { padding: 10px 0; }
.m-text-field--comfortable .m-text-field__input { padding: 8px 0; }
.m-text-field--compact .m-text-field__input { padding: 4px 0; font-size: var(--m-font-size-sm); }

.m-text-field__input::placeholder { color: var(--m-on-surface-variant); opacity: 0.6; }

.m-text-field__clear,
.m-text-field__prepend-inner,
.m-text-field__append-inner {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--m-on-surface-variant);
  padding: 0;
  flex-shrink: 0;
}

.m-text-field__prefix,
.m-text-field__suffix {
  display: flex;
  align-items: center;
  color: var(--m-on-surface-variant);
  font-size: 0.95em;
  flex-shrink: 0;
  user-select: none;
}
.m-text-field__prefix { margin-right: 4px; }
.m-text-field__suffix { margin-left: 4px; }
.m-text-field__clear:hover,
.m-text-field__append-inner:hover { color: var(--m-on-surface); }

.m-text-field__prepend,
.m-text-field__append {
  display: flex;
  align-items: center;
  color: var(--m-on-surface-variant);
  cursor: pointer;
  margin-top: 28px;
}

.m-text-field--single-line .m-text-field__prepend,
.m-text-field--single-line .m-text-field__append {
  margin-top: 0;
}

.m-text-field__details {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: var(--m-space-1) var(--m-space-3) 0;
  font-size: var(--m-font-size-xs);
  min-height: 20px;
}

.m-text-field__error { color: var(--m-error); }
.m-text-field__hint { color: var(--m-on-surface-variant); }
.m-text-field__counter { margin-left: auto; color: var(--m-on-surface-variant); }

.m-text-field--disabled { opacity: 0.5; pointer-events: none; }

.m-text-field__loader { height: 2px; overflow: hidden; margin-top: -1px; }
.m-text-field__progress {
  height: 100%;
  background: var(--m-primary);
  animation: m-tf-loading 1.5s ease-in-out infinite;
}
@keyframes m-tf-loading {
  0% { width: 0; margin-left: 0; }
  50% { width: 60%; margin-left: 20%; }
  100% { width: 0; margin-left: 100%; }
}
</style>
